import { useEffect, useState } from 'react';
import { api, errMsg } from '../api/client';
import { Button, Field, Input, Modal, useToast } from '../ui';

interface Status {
  enabled: boolean;
  recoveryRemaining: number;
}
interface SetupData {
  secret: string;
  otpauth: string;
  qrSvg: string;
}

/**
 * Two-factor (TOTP) self-service — enrol with an authenticator app, or turn it off. All server-side, no
 * email/SMS. Enrol flow: setup (scan QR / paste key) → verify a code → shown one-time recovery codes.
 */
export function TwoFactorModal({ onClose }: { onClose: () => void }) {
  const toast = useToast();
  const [status, setStatus] = useState<Status | null>(null);
  const [setup, setSetup] = useState<SetupData | null>(null);
  const [code, setCode] = useState('');
  const [recovery, setRecovery] = useState<string[] | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    void api<Status>('/api/me/2fa').then((r) => {
      if (r.ok) setStatus(r.body as Status);
    });
  }, []);

  async function startSetup() {
    setError('');
    setBusy(true);
    const r = await api<SetupData>('/api/me/2fa/setup', { method: 'POST' });
    setBusy(false);
    if (!r.ok) return setError(errMsg(r));
    setSetup(r.body as SetupData);
  }

  async function enable() {
    setError('');
    setBusy(true);
    const r = await api<{ recoveryCodes: string[] }>('/api/me/2fa/enable', {
      method: 'POST',
      body: JSON.stringify({ token: code.trim() }),
    });
    setBusy(false);
    if (!r.ok) return setError(errMsg(r));
    setRecovery((r.body as { recoveryCodes: string[] }).recoveryCodes);
    setSetup(null);
    setCode('');
  }

  async function disable() {
    setError('');
    setBusy(true);
    const r = await api('/api/me/2fa/disable', {
      method: 'POST',
      body: JSON.stringify({ token: code.trim() }),
    });
    setBusy(false);
    if (!r.ok) return setError(errMsg(r));
    toast('تأیید دومرحله‌ای غیرفعال شد');
    onClose();
  }

  // Step 3 — enrolment succeeded; show the one-time recovery codes.
  if (recovery) {
    return (
      <Modal
        title="کدهای بازیابی"
        onClose={onClose}
        actions={
          <Button
            onClick={() => {
              toast('تأیید دومرحله‌ای فعال شد');
              onClose();
            }}
          >
            ذخیره کردم
          </Button>
        }
      >
        <p style={{ lineHeight: 1.9 }}>
          تأیید دومرحله‌ای فعال شد. این کدها را جایی امن نگه دارید — اگر به برنامهٔ احرازکننده
          دسترسی نداشتید، هر کد <b>یک‌بار</b> برای ورود کار می‌کند. پس از بستن دوباره نشان داده
          نمی‌شوند.
        </p>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: 8,
            fontFamily: 'monospace',
            fontSize: '1.05em',
            margin: '10px 0',
            direction: 'ltr',
          }}
        >
          {recovery.map((c) => (
            <div
              key={c}
              style={{ padding: '6px 10px', background: 'var(--line)', borderRadius: 6 }}
            >
              {c}
            </div>
          ))}
        </div>
      </Modal>
    );
  }

  // Step 2 — a secret was staged; show the QR + key and verify a code.
  if (setup) {
    return (
      <Modal
        title="افزودن به برنامهٔ احرازکننده"
        onClose={onClose}
        actions={
          <>
            <Button variant="secondary" onClick={onClose}>
              انصراف
            </Button>
            <Button onClick={enable} loading={busy}>
              فعال‌سازی
            </Button>
          </>
        }
      >
        <p style={{ lineHeight: 1.9, margin: '0 0 8px' }}>
          با Google Authenticator / Microsoft Authenticator این کد را اسکن کنید — یا کلید زیر را
          دستی وارد نمایید — سپس کد ۶ رقمی را برای تأیید بزنید.
        </p>
        <div
          style={{ display: 'flex', justifyContent: 'center', margin: '8px 0' }}
          // The QR is a server-rendered static SVG of the otpauth URI (no user input in it).
          dangerouslySetInnerHTML={{ __html: setup.qrSvg }}
        />
        <div
          style={{
            textAlign: 'center',
            fontFamily: 'monospace',
            direction: 'ltr',
            wordBreak: 'break-all',
            color: 'var(--ink-2)',
            margin: '0 0 12px',
          }}
        >
          {setup.secret}
        </div>
        <Field label="کد ۶ رقمی" htmlFor="t2c" error={error || undefined}>
          <Input
            id="t2c"
            inputMode="numeric"
            autoComplete="one-time-code"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            autoFocus
          />
        </Field>
      </Modal>
    );
  }

  // Step 1 — status / entry point.
  return (
    <Modal
      title="تأیید دومرحله‌ای (2FA)"
      onClose={onClose}
      actions={
        status?.enabled ? (
          <Button variant="danger" onClick={disable} loading={busy}>
            غیرفعال‌سازی
          </Button>
        ) : (
          <>
            <Button variant="secondary" onClick={onClose}>
              بستن
            </Button>
            <Button onClick={startSetup} loading={busy}>
              فعال‌سازی
            </Button>
          </>
        )
      }
    >
      {!status ? (
        <p className="muted">در حال بارگذاری…</p>
      ) : status.enabled ? (
        <>
          <p style={{ lineHeight: 1.9 }}>
            تأیید دومرحله‌ای <b style={{ color: 'var(--green)' }}>فعال</b> است. ورود شما علاوه بر
            گذرواژه به کد برنامهٔ احرازکننده نیاز دارد. کدهای بازیابی باقی‌مانده:{' '}
            {status.recoveryRemaining}.
          </p>
          <Field
            label="برای غیرفعال‌سازی، یک کد فعلی یا کد بازیابی وارد کنید"
            htmlFor="t2d"
            error={error || undefined}
          >
            <Input
              id="t2d"
              inputMode="numeric"
              autoComplete="one-time-code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
            />
          </Field>
        </>
      ) : (
        <p style={{ lineHeight: 1.9 }}>
          یک عامل دوم به حساب شما اضافه می‌کند: هنگام ورود، علاوه بر گذرواژه، یک کد یک‌بارمصرف از
          برنامهٔ احرازکننده روی گوشی‌تان لازم می‌شود. نیازی به ایمیل یا پیامک نیست.
        </p>
      )}
    </Modal>
  );
}
